import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import Actions from '@components/Actions';
import api from '@services/api';
import ActionsItems from '@components/Actions/ActionsItems';
import { DeviceActionProps } from '@components/Actions/ActionsModal/types';

export interface SimcardActionsModalProps {
  visible: boolean;
  onClose: (cancelled?: boolean) => void;
  actionRole: RolesTypes | undefined;
  selectedDevices: any;
  isUpload?: boolean;
  showContracted?: boolean;
}

const SimcardActionsModal = ({
  visible,
  onClose,
  actionRole,
  selectedDevices,
  isUpload,
  showContracted,
}: SimcardActionsModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('device-actions-modal');
  const { selectedApp, hasMotLorawan } = useContext(SessionContext);
  const [form] = Form.useForm();
  const uploadedItems = Form.useWatch('uploadedItems', form);
  const [sendDeviceLoading, setSendDeviceLoading] = useState(false);

  const sendDeviceAction = (
    payload:
      | {
          action?: number;
          devices?: { [key: string]: any }[];
          customerIds?: { customerId: string }[] | number[];
        }
      | undefined,
  ) => api.post('/service-proxy/actions', payload);

  const devices = useMemo(
    () => (isUpload ? uploadedItems : selectedDevices),
    [isUpload, selectedDevices, uploadedItems],
  );

  const currentIccid = devices?.[0]?.iccid;
  const currentMsisdn = devices?.[0]?.msisdn;

  const activationAction = ActionsItems.Common.useActivation({
    devices,
    form,
  });

  const networkResetAction = ActionsItems.Common.useNetworkReset({
    devices,
    isUpload,
  });

  const simcardRestAction = ActionsItems.Common.useSimcardReset({
    devices,
    isUpload,
  });

  const crrBlockUnlockAction = ActionsItems.Common.useCrrBlockUnlock({
    devices,
    isUpload,
    form,
  });

  const nicknameChangeAction = ActionsItems.Common.useNicknameChange({
    devices,
    isUpload,
    form,
  });

  const networkBlockUnlockAction = ActionsItems.Common.useNetworkBlockUnlock({
    devices,
    isUpload,
    form,
  });

  const brokerCancellationAction = ActionsItems.MoT.useBrokerCancellation({
    devices,
    form,
    isUpload,
  });

  const suspensionWithdrawalAction = ActionsItems.MoT.useSuspensionWithdrawal({
    devices,
    form,
    isUpload,
  });

  const addCreditAction = ActionsItems.MoT.useAddCredit({
    devices,
    form,
    isUpload,
    actionRole,
    showContracted: !!showContracted,
  });

  const msisdnExchangeAction = ActionsItems.MoT.useExchangeStep({
    type: 'MSISDN',
    devices,
    form,
    isUpload,
    role: rolesConstants.MSISDN_EXCHANGE,
    selectedRole: actionRole,
    currentValue: currentMsisdn,
  });

  const iccidExchangeAction = ActionsItems.MoT.useExchangeStep({
    type: 'ICCID',
    devices,
    form,
    isUpload,
    role: rolesConstants.PLASTIC_EXCHANGE,
    selectedRole: actionRole,
    currentValue: currentIccid,
  });

  const forceReactivationAction = ActionsItems.MoT.useForceReactivation({
    devices,
    isUpload,
  });

  const imeiLockUnlockAction = ActionsItems.MoT.useImeiLockUnlock({
    devices,
    isUpload,
    form,
  });

  const manualActivationAction = ActionsItems.MoT.useManualActivation({
    devices,
    isUpload,
  });

  const motDeactivationAction = ActionsItems.MoT.useMotDeactivation({
    devices,
    isUpload,
  });

  const motOperatorChangeAction = ActionsItems.MoT.useMotOperatorChange({
    devices,
    isUpload,
    form,
    actionRole,
  });

  const financialLockUnlockAction = ActionsItems.MoT.useFinancialLockUnlock({
    customerIds: devices,
    isUpload,
    form,
  });

  const transferAction = ActionsItems.MoT.useTransfer({
    devices,
    isUpload,
    form,
  });

  const changeApnAction = ActionsItems.MoT.useChangeApn({
    devices,
    isUpload,
    form,
  });

  const useHlrReprog = ActionsItems.Common.useHlrReprog({
    devices,
    isUpload,
  });

  const algarSuspension = ActionsItems.Common.useAlgarSuspension({
    devices,
    isUpload,
    form,
  });

  const baitRenewalAction = ActionsItems.MoT.useBaitRenewal({
    devices,
    isUpload,
  });

  const syncApnAction = ActionsItems.Common.useSyncApn({
    devices,
  });

  const cancellationAction = ActionsItems.Common.useCancellation({
    form,
    devices,
    isUpload,
  });

  const operatorRestrictionAction = ActionsItems.Common.useOperatorRestriction({
    devices,
    isUpload,
    form,
    actionRole,
  });

  const simcardChangeAction = ActionsItems.Common.useSimcardChange({
    devices,
    form,
    isUpload,
  });

  const selectedAction = useMemo((): DeviceActionProps | undefined => {
    const actions: { [key: string]: DeviceActionProps } = {
      [rolesConstants.SIMCARD_IMPORT]: activationAction,
      [rolesConstants.NETWORK_RESET]: networkResetAction,
      [rolesConstants.SIMCARD_RESET]: simcardRestAction,
      [rolesConstants.SMS_BLOCK]: crrBlockUnlockAction,
      [rolesConstants.NICKNAME_CHANGE]: nicknameChangeAction,
      [rolesConstants.NETWORK_BLOCK_UNLOCK]: networkBlockUnlockAction,
      [rolesConstants.BROKER_CANCELLATION]: brokerCancellationAction,
      [rolesConstants.SUSPENSION_WITHDRAWAL]: suspensionWithdrawalAction,
      [rolesConstants.ADD_CREDIT_TO_SIMCARD]: addCreditAction,
      [rolesConstants.MSISDN_EXCHANGE]: msisdnExchangeAction,
      [rolesConstants.PLASTIC_EXCHANGE]: iccidExchangeAction,
      [rolesConstants.FORCE_REACTIVATION]: forceReactivationAction,
      [rolesConstants.IMEI_LOCK_UNLOCK]: imeiLockUnlockAction,
      [rolesConstants.MANUAL_ACTIVATION]: manualActivationAction,
      [rolesConstants.MOT_DEACTIVATION]: motDeactivationAction,
      [rolesConstants.MOT_OPERATOR_CHANGE]: motOperatorChangeAction,
      [rolesConstants.FINANCIAL_LOCK_UNLOCK]: financialLockUnlockAction,
      [rolesConstants.TRANSFER]: transferAction,
      [rolesConstants.CHANGE_APN]: changeApnAction,
      [rolesConstants.HLR_REPROG]: useHlrReprog,
      [rolesConstants.CONTRACT_SUSPEND]: algarSuspension,
      [rolesConstants.BAIT_RENEWAL]: baitRenewalAction,
      [rolesConstants.SYNC_APN]: syncApnAction,
      [rolesConstants.CANCELLATION]: cancellationAction,
      [rolesConstants.OPERATOR_RESTRICTION]: operatorRestrictionAction,
      [rolesConstants.CHANGE_SIMCARD]: simcardChangeAction,
    };

    return actionRole && actions[actionRole];
  }, [
    activationAction,
    networkResetAction,
    simcardRestAction,
    crrBlockUnlockAction,
    nicknameChangeAction,
    networkBlockUnlockAction,
    brokerCancellationAction,
    suspensionWithdrawalAction,
    addCreditAction,
    msisdnExchangeAction,
    iccidExchangeAction,
    forceReactivationAction,
    imeiLockUnlockAction,
    manualActivationAction,
    motDeactivationAction,
    motOperatorChangeAction,
    financialLockUnlockAction,
    transferAction,
    changeApnAction,
    useHlrReprog,
    algarSuspension,
    baitRenewalAction,
    syncApnAction,
    cancellationAction,
    operatorRestrictionAction,
    simcardChangeAction,
    actionRole,
  ]);

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}/${
      hasMotLorawan ? 'mobile-network/' : ''
    }operational/actions-queue?actionType=${
      selectedAction?.payloadToSend?.action
    }`;
    message.success(
      <>
        <span>{t('msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('msgs.follow-in-the-actions-queue')}
        </Button>
      </>,
      2,
    );
  }, [
    selectedApp,
    hasMotLorawan,
    selectedAction?.payloadToSend?.action,
    t,
    navigate,
  ]);

  const handleOnClose = useCallback(() => {
    onClose();
    selectedAction?.onClose?.();
    form.resetFields();
  }, [form, onClose, selectedAction]);

  const handleActionConfirm = useCallback(async () => {
    setSendDeviceLoading(true);
    try {
      if (selectedAction?.sendDeviceAction) {
        selectedAction?.sendDeviceAction?.(handleOnClose);
      } else {
        await sendDeviceAction(selectedAction?.payloadToSend);
        handleSuccess();
      }
    } catch {
      message.error(t('msgs.error-when-requesting'));
    }
    handleOnClose();
    setSendDeviceLoading(false);
  }, [handleOnClose, selectedAction, handleSuccess, t]);

  return (
    <Actions.Modal
      form={form}
      isOpen={visible}
      isUpload={isUpload}
      onClose={handleOnClose}
      onConfirm={handleActionConfirm}
      onConfirmLoading={sendDeviceLoading}
      selectedAction={selectedAction}
    />
  );
};

export default SimcardActionsModal;
